import { DatePipe } from '@angular/common';
import { Component, effect, ElementRef, inject, input, OnDestroy, output, viewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterModule } from '@angular/router';
import { IIconIdForm } from '@campaign-form/campaign-form.interface';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { HasPermissionPipe } from '@core/pipes/has-permission.pipe';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { EEventType } from '@enums/event-tracking.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ICustomer } from '@pages/customers/customers.interface';
import { CustomersService } from '@pages/customers/customers.service';
import { BannerCardComponent } from '@shared/components/banner-card/banner-card.component';
import { BaseComponent } from '@shared/components/base.component';
import { BreadcrumbComponent, IBreadcrumb } from '@shared/components/breadcrumb/breadcrumb.component';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { ContentContainerComponent } from '@shared/components/content-container/content-container.component';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { FileUploaderComponent } from '@shared/components/file-uploader/file-uploader.component';
import { FileUploaderService } from '@shared/components/file-uploader/file-uploader.service';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { ImageUtilitiesService } from '@utilities/image-utilities.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { filter, Subscription } from 'rxjs';

@Component({
  imports: [
    RouterModule,
    MatButtonModule,
    MatMenuModule,
    HasPermissionDirective,
    FormsModule,
    BannerCardComponent,
    TranslateModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatErrorComponent,
    FileUploaderComponent,
    DatePipe,
    ContentContainerComponent,
    HasPermissionPipe,
    NgxSkeletonLoaderModule,
    IconComponent,
    DotsSpinnerComponent,
  ],
  selector: 'app-customer-details-banner',
  templateUrl: './customer-details-banner.component.html',
})
export class CustomerDetailsBannerComponent extends BaseComponent implements OnDestroy {
  public readonly customer = input<ICustomer | null>(null);
  public readonly nameForm = input.required<FormGroup<{ name: FormControl<string | null> }>>();
  public readonly iconForm = input.required<FormGroup<IIconIdForm>>();
  public readonly isEditingName = input<boolean>(false);
  public readonly nameSaving = input<boolean>(false);
  public readonly nameErrorMessage = input<string | null>(null);

  public readonly nameEditingChanged = output<boolean>();
  public readonly imageSelected = output<{ file: File; id: string }>();
  public readonly imageRemoved = output<void>();
  public readonly nameSaveRequested = output<void>();

  public breadcrumbs!: IBreadcrumb[];

  private readonly nameInput = viewChild<ElementRef<HTMLInputElement>>('nameInput');
  private readonly fileUploaderComponent = viewChild<FileUploaderComponent>('fileUploaderComponent');

  private readonly dialog = inject(MatDialog);
  private readonly customersService = inject(CustomersService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly eventTrackingService = inject(EventTrackingService);

  private readonly subscriptions: Subscription[] = [];

  constructor() {
    super();

    effect(() => {
      this.updateBreadcrumbs();
    });

    this.nameFormSubscription();
  }

  public onChangeNameClick(): void {
    this.nameForm().patchValue({ name: this.customer()?.name ?? '' });
    this.nameEditingChanged.emit(true);

    setTimeout(() => {
      this.nameInput()?.nativeElement.focus();
    });
  }

  public onImageSelected(event: { file: File; id: string }): void {
    const imageFileControl = this.iconForm().get('imageFile');
    const iconControl = this.iconForm().get('iconId');

    imageFileControl?.setValue(event.file);
    imageFileControl?.markAsDirty();
    imageFileControl?.updateValueAndValidity();
    iconControl?.setValue(event.id);

    if (imageFileControl?.valid) {
      this.imageSelected.emit(event);
    }
  }

  public onImageRemoved(): void {
    const imageFileControl = this.iconForm().get('imageFile');
    const iconControl = this.iconForm().get('iconId');

    imageFileControl?.setValue(null);
    imageFileControl?.markAsDirty();
    imageFileControl?.updateValueAndValidity();
    iconControl?.setValue(null);

    this.imageRemoved.emit();
  }

  public onChangeImageClick(): void {
    const fileUploader = this.fileUploaderComponent();

    if (fileUploader) {
      fileUploader.onUploadNewClick();
    }
  }

  public onDeleteCustomerClick(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('shared.cancel'),
        confirmText: this.translate.instant('shared.delete'),
        message: this.translate.instant('pages.customers.datatable.deleteCustomerMessage', {
          name: this.customer()?.name,
        }),
        title: this.translate.instant('pages.customers.datatable.deleteCustomer'),
      },
      width: '400px',
    });

    dialogRef
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(() => {
        const customerId = this.customer()?.id!;

        this.customersService.deleteCustomer(customerId).subscribe(() => {
          this.eventTrackingService.track(EEventType.customerDeleted, {
            customerId,
            source: 'customer_details_banner',
          });
          this.router.navigate(['/customers']);
        });
      });
  }

  public onSaveClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.customer() || !this.nameForm().valid || this.nameSaving()) {
      return;
    }

    this.nameSaveRequested.emit();
  }

  public onDiscardClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.nameSaving()) {
      return;
    }

    this.nameForm().patchValue({ name: this.customer()?.name ?? '' });
    this.nameEditingChanged.emit(false);
  }

  public setCustomer(customer: ICustomer | null, emitEvent = true): void {
    if (customer) {
      this.iconForm().patchValue(
        {
          iconId: customer.iconId,
          imageFile: ImageUtilitiesService.isValidFileId(customer.iconId)
            ? FileUploaderService.createCustomFile(customer.iconId!)
            : null,
        },
        { emitEvent },
      );
    }

    this.updateBreadcrumbs();
  }

  public updateBreadcrumbs(): void {
    this.breadcrumbs = [
      { label: this.translate.instant('pages.customers.banner.title'), link: '/customers' },
      { label: this.customer()?.name ?? BreadcrumbComponent.loadingLabel },
    ];
  }

  public ngOnDestroy(): void {
    this.subscriptions?.forEach((subscription) => subscription?.unsubscribe());
  }

  private nameFormSubscription(): void {
    effect(() => {
      const subscription = this.nameForm()
        .get('name')
        ?.valueChanges.subscribe((name) => {
          if (this.isEditingName() && (!name || name.trim() === '')) {
            this.nameForm().patchValue({ name: '' }, { emitEvent: false });

            return;
          }
        });

      if (subscription) {
        this.subscriptions.push(subscription);
      }
    });
  }
}
